import React, { useEffect, useState } from 'react';
import { Modal, Button, Space, Typography, Card, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { purgeIncidentData } from '../../redux/store';
import { 
  ExclamationCircleOutlined, 
  ClockCircleOutlined,
  CarOutlined,
  UserOutlined,
  FileTextOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

interface SessionRecoveryProps {
  onComplete?: () => void;
}

const SessionRecovery: React.FC<SessionRecoveryProps> = ({ onComplete }) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Check Redux state for incomplete incident
  const vehicle = useAppSelector(state => state.vehicle);
  const operatorInfo = useAppSelector(state => state.operatorInfo);
  const incidentDetails = useAppSelector(state => state.incidentDetails);
  const vehiclePhotos = useAppSelector(state => state.vehiclePhotos);
  const damageLocations = useAppSelector(state => state.damageLocations);
  
  // Determine if there's an incomplete incident
  const hasIncompleteIncident = () => {
    // Check if any incident data exists but isn't finalized
    return (
      (vehicle.vin || vehicle.vehicleNumber || operatorInfo.operatorId || 
       Object.keys(vehiclePhotos.photoMap).length > 0 || 
       Object.keys(damageLocations).length > 0) &&
      !incidentDetails.isFinalized
    );
  };
  
  // Calculate progress percentage
  const calculateProgress = () => {
    let totalSteps = 7;
    let completedSteps = 0;
    
    if (vehicle.vin || vehicle.vehicleNumber) completedSteps++;
    if (vehicle.vehicleId) completedSteps++;
    if (operatorInfo.operatorId) completedSteps++;
    if (Object.keys(vehiclePhotos.photoMap).length > 0) completedSteps++;
    if (Object.keys(damageLocations).length > 0) completedSteps++;
    if (incidentDetails.incidentType) completedSteps++;
    if (incidentDetails.isFinalized) completedSteps++;
    
    return Math.round((completedSteps / totalSteps) * 100);
  };
  
  // Get a summary of the incomplete incident
  const getIncidentSummary = () => {
    const summary = {
      vehicle: vehicle.vehicleNumber || vehicle.vin || 'Not specified',
      operator: operatorInfo.firstName && operatorInfo.lastName 
        ? `${operatorInfo.firstName} ${operatorInfo.lastName}`
        : operatorInfo.email || 'Not specified',
      photoCount: Object.keys(vehiclePhotos.photoMap).length,
      incidentType: incidentDetails.incidentType || 'Not specified',
      incidentDate: incidentDetails.incidentDate 
        ? new Date(incidentDetails.incidentDate).toLocaleDateString()
        : 'Not specified',
      progress: calculateProgress()
    };
    
    return summary;
  };
  
  useEffect(() => {
    // Check for incomplete incident on component mount
    const checkSession = () => {
      if (hasIncompleteIncident()) {
        // Delay showing modal to ensure app is fully loaded
        setTimeout(() => {
          setVisible(true);
        }, 1000);
      }
    };
    
    checkSession();
  }, []);
  
  const handleResume = () => {
    setLoading(true);
    // Navigate to incident creation screen to resume
    navigate('/incident/create');
    setVisible(false);
    onComplete?.();
  };
  
  const handleStartNew = () => {
    setLoading(true);
    // Clear incident data and start fresh
    dispatch(purgeIncidentData());
    navigate('/incident/create');
    setVisible(false);
    onComplete?.();
  };
  
  const handleCancel = () => {
    setVisible(false);
    onComplete?.();
  };
  
  if (!visible) return null;
  
  const summary = getIncidentSummary();
  
  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          <span>Incomplete Incident Detected</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
    >
      <div style={{ marginBottom: 20 }}>
        <Text>
          We found an incomplete incident report. Would you like to continue where you left off?
        </Text>
      </div>
      
      <Card 
        size="small" 
        style={{ marginBottom: 20, backgroundColor: '#f5f5f5' }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Space>
              <CarOutlined />
              <Text strong>Vehicle:</Text>
            </Space>
            <Text>{summary.vehicle}</Text>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Space>
              <UserOutlined />
              <Text strong>Operator:</Text>
            </Space>
            <Text>{summary.operator}</Text>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Space>
              <FileTextOutlined />
              <Text strong>Type:</Text>
            </Space>
            <Text>{summary.incidentType}</Text>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Space>
              <ClockCircleOutlined />
              <Text strong>Date:</Text>
            </Space>
            <Text>{summary.incidentDate}</Text>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text strong>Photos Captured:</Text>
            <Tag color={summary.photoCount > 0 ? 'green' : 'default'}>
              {summary.photoCount}
            </Tag>
          </div>
          
          <div style={{ marginTop: 10 }}>
            <Text strong>Progress: {summary.progress}%</Text>
            <div 
              style={{ 
                width: '100%', 
                height: 8, 
                backgroundColor: '#e0e0e0', 
                borderRadius: 4,
                marginTop: 5
              }}
            >
              <div 
                style={{ 
                  width: `${summary.progress}%`, 
                  height: '100%', 
                  backgroundColor: '#1890ff',
                  borderRadius: 4,
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
          </div>
        </Space>
      </Card>
      
      <Space direction="vertical" style={{ width: '100%' }}>
        <Button 
          type="primary" 
          block 
          size="large"
          loading={loading}
          onClick={handleResume}
          icon={<ClockCircleOutlined />}
        >
          Resume Incident Report
        </Button>
        
        <Button 
          block 
          size="large"
          loading={loading}
          onClick={handleStartNew}
          danger
        >
          Start New Report (Discard Current)
        </Button>
        
        <Button 
          block 
          onClick={handleCancel}
          disabled={loading}
        >
          Continue to Home
        </Button>
      </Space>
    </Modal>
  );
};

export default SessionRecovery;